import { ElementRef, ViewChild, Directive } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import moment from 'moment';
import { StorageService } from 'src/app/services/StorageService/storage.service';
import {
  HistogramBucket,
  KpiCard,
  LineSeries,
  PieSlice
} from './dashboard-operacional.mock';

@Directive()
export abstract class DashboardBaseComponent {
  @ViewChild('dashboardPdfContent') dashboardPdfContent!: ElementRef<HTMLElement>;

  abstract readonly dashboardTitle: string;
  abstract readonly pdfFileNamePrefix: string;
  abstract readonly dashboardStorageKey: string;

  readonly prototypeBanner = '';

  isLoading = false;
  isExportingPdf = false;
  pdfGeneratedAt: Date | null = null;
  lineHoverIndex: number | null = null;
  sectionCollapsed: Record<string, boolean> = {};

  readonly lineChartWidth = 280;
  readonly lineChartHeight = 120;
  readonly lineChartPadding = 12;

  abstract kpis: KpiCard[];

  form: FormGroup;

  constructor(
    protected fb: FormBuilder,
    protected storageService?: StorageService
  ) {
    this.form = this.fb.group({
      DT_INIC: [null as string | null],
      DT_FIM: [null as string | null]
    });
  }

  protected initSectionCollapse(defaultSections: Record<string, boolean>): void {
    const saved = this.storageService?.retornarSessao<Record<string, boolean>>(
      this.dashboardStorageKey
    );
    this.sectionCollapsed = { ...defaultSections, ...saved };
  }

  isSectionCollapsed(sectionId: string): boolean {
    return this.sectionCollapsed[sectionId] ?? false;
  }

  toggleSection(sectionId: string): void {
    this.sectionCollapsed = {
      ...this.sectionCollapsed,
      [sectionId]: !this.isSectionCollapsed(sectionId)
    };
    this.storageService?.armazenarSessao(this.dashboardStorageKey, this.sectionCollapsed);
  }

  formatCurrency(value: number): string {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  maxBarValue(items: { value: number }[]): number {
    return Math.max(...items.map(i => i.value), 1);
  }

  barHeight(value: number, items: { value: number }[]): number {
    return Math.round((value / this.maxBarValue(items)) * 100);
  }

  pieSlicePercent(value: number, slices: PieSlice[]): number {
    const total = slices.reduce((sum, s) => sum + s.value, 0);
    if (total <= 0) {
      return 0;
    }
    return Math.round((value / total) * 100);
  }

  hasChartData(items: { value: number }[]): boolean {
    return items.some(item => item.value > 0);
  }

  pieGradient(slices: PieSlice[]): string {
    const total = slices.reduce((sum, s) => sum + s.value, 0);
    if (total <= 0) {
      return 'conic-gradient(#e2e8f0 0% 100%)';
    }
    let cursor = 0;
    const parts = slices.map(slice => {
      const start = (cursor / total) * 100;
      cursor += slice.value;
      const end = (cursor / total) * 100;
      return `${slice.color} ${start}% ${end}%`;
    });
    return `conic-gradient(${parts.join(', ')})`;
  }

  lineChartPoints(values: number[], labels: string[]): string {
    return values
      .map((value, index) => {
        const { x, y } = this.lineChartPoint(value, index, values, labels);
        return `${x},${y}`;
      })
      .join(' ');
  }

  setLineHover(index: number): void {
    this.lineHoverIndex = index;
  }

  clearLineHover(): void {
    this.lineHoverIndex = null;
  }

  lineChartValueBounds(series: LineSeries[]): { min: number; max: number } {
    const allValues = series.flatMap(s => s.values);
    return {
      min: Math.min(...allValues, 0),
      max: Math.max(...allValues, 1)
    };
  }

  lineChartPoint(
    value: number,
    index: number,
    values: number[],
    labels: string[]
  ): { x: number; y: number } {
    const series = [{ values }] as LineSeries[];
    const { min, max } = this.lineChartValueBounds(series);
    const range = max - min || 1;
    const stepX =
      (this.lineChartWidth - this.lineChartPadding * 2) /
      Math.max(labels.length - 1, 1);
    const x = this.lineChartPadding + index * stepX;
    const y =
      this.lineChartHeight -
      this.lineChartPadding -
      ((value - min) / range) * (this.lineChartHeight - this.lineChartPadding * 2);

    return { x, y };
  }

  lineChartPointForSeries(
    value: number,
    index: number,
    series: LineSeries[],
    labels: string[]
  ): { x: number; y: number } {
    const { min, max } = this.lineChartValueBounds(series);
    const range = max - min || 1;
    const stepX =
      (this.lineChartWidth - this.lineChartPadding * 2) /
      Math.max(labels.length - 1, 1);
    const x = this.lineChartPadding + index * stepX;
    const y =
      this.lineChartHeight -
      this.lineChartPadding -
      ((value - min) / range) * (this.lineChartHeight - this.lineChartPadding * 2);

    return { x, y };
  }

  lineHoverGuideX(labels: string[]): number {
    if (this.lineHoverIndex === null) {
      return 0;
    }

    return this.lineChartPoint(0, this.lineHoverIndex, [0], labels).x;
  }

  lineHoverZoneX(index: number, labels: string[]): number {
    return (index / labels.length) * this.lineChartWidth;
  }

  lineHoverZoneWidth(labels: string[]): number {
    return this.lineChartWidth / labels.length;
  }

  lineTooltipLeftPercent(labels: string[]): number {
    if (this.lineHoverIndex === null) {
      return 0;
    }

    return (this.lineHoverGuideX(labels) / this.lineChartWidth) * 100;
  }

  lineHoverValue(series: LineSeries): number {
    if (this.lineHoverIndex === null) {
      return 0;
    }

    return series.values[this.lineHoverIndex];
  }

  histogramPercent(bucket: HistogramBucket, buckets: HistogramBucket[]): number {
    const max = Math.max(...buckets.map(b => b.value), 1);
    return Math.round((bucket.value / max) * 100);
  }

  async gerarPDF(): Promise<void> {
    if (this.isExportingPdf || !this.dashboardPdfContent) {
      return;
    }

    this.isExportingPdf = true;
    this.clearLineHover();
    this.pdfGeneratedAt = new Date();

    try {
      await new Promise(resolve => setTimeout(resolve, 200));

      const element = this.dashboardPdfContent.nativeElement;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: element.scrollWidth,
        height: element.scrollHeight,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });

      const pdf = new jsPDF('l', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - margin * 2;
      const printableHeight = pageHeight - margin * 2;
      const pageCanvasHeight = Math.max(1, Math.floor((canvas.width * printableHeight) / contentWidth));
      let renderedHeight = 0;
      let pageIndex = 0;

      while (renderedHeight < canvas.height) {
        const sliceHeight = Math.min(pageCanvasHeight, canvas.height - renderedHeight);
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;

        const ctx = pageCanvas.getContext('2d');
        if (!ctx) {
          break;
        }

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(
          canvas,
          0,
          renderedHeight,
          canvas.width,
          sliceHeight,
          0,
          0,
          canvas.width,
          sliceHeight
        );

        const pageImgData = pageCanvas.toDataURL('image/png');
        const pageImgHeight = (sliceHeight * contentWidth) / canvas.width;

        if (pageIndex > 0) {
          pdf.addPage();
        }

        pdf.addImage(pageImgData, 'PNG', margin, margin, contentWidth, pageImgHeight);
        renderedHeight += sliceHeight;
        pageIndex++;
      }

      const periodSuffix = this.buildPeriodSuffix();
      pdf.save(`${this.pdfFileNamePrefix}${periodSuffix}.pdf`);
    } finally {
      this.isExportingPdf = false;
    }
  }

  private buildPeriodSuffix(): string {
    const ref = this.form.controls['DT_REF']?.value;
    if (ref) {
      const formatted = ref.includes('T')
        ? moment(ref).format('DD-MM-YYYY')
        : moment(ref, 'YYYY-MM-DD').format('DD-MM-YYYY');
      return `_${formatted}`;
    }

    const start = this.form.controls['DT_INIC']?.value;
    const end = this.form.controls['DT_FIM']?.value;

    if (!start && !end) {
      return '';
    }

    const format = (value: string | null) =>
      value ? moment(value).format('DD-MM-YYYY') : 'inicio';

    return `_${format(start)}_${format(end)}`;
  }

  formatFilterDate(value: string | null): string {
    if (!value) {
      return '—';
    }

    return moment(value).format('DD/MM/YYYY HH:mm');
  }
}
